import React, { useState, useEffect } from 'react';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardAvailability, setCardAvailability] = useState([]);
  const [creatingCards, setCreatingCards] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [gateways, setGateways] = useState([]);
  const [activeCreationJobs, setActiveCreationJobs] = useState([]);
  const [scheduleData, setScheduleData] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  useEffect(() => {
    fetchJobs();
    fetchGateways();
    fetchActiveCreationJobs();
    const interval = setInterval(() => {
      fetchJobs();
      fetchActiveCreationJobs();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchActiveCreationJobs = async () => {
    try {
      const response = await fetch('/api/cardholder-jobs', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setActiveCreationJobs(data.filter(j => j.status === 'running'));
      }
    } catch (err) {
      console.error('Error fetching creation jobs:', err);
    }
  };

  const fetchGateways = async () => {
    try {
      const response = await fetch('/api/gateways', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setGateways(data);
      }
    } catch (err) {
      console.error('Error fetching gateways:', err);
    }
  };

  const deleteJob = async (jobId) => {
    if (!confirm('Delete this job? This action cannot be undone.')) return;

    setDeleting(jobId);
    try {
      const response = await fetch(`/api/jobs/${jobId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error);
      }

      alert('Job deleted successfully');
      fetchJobs();
      if (selectedJob?.id === jobId) {
        setSelectedJob(null);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    if (!selectedJob) return;

    const jobId = selectedJob.id;
    const jobStatus = selectedJob.status;

    fetchJobDetails(jobId);
    if (jobStatus === 'draft' || jobStatus === 'pending') {
      fetchCardAvailability(jobId);
    }

    const interval = setInterval(() => {
      fetchJobDetails(jobId);
      if (jobStatus === 'draft' || jobStatus === 'pending') {
        fetchCardAvailability(jobId);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedJob?.id]);

  const fetchJobs = async () => {
    try {
      const response = await fetch('/api/jobs', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobDetails = async (jobId) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSelectedJob(data);
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
    }
  };

  const getMissingCardHoldersInfo = () => {
    if (!selectedJob || !logs) return null;
    
    const lastLog = logs.find(l => l.type === 'warning' && l.message.includes('CardHolders needed'));
    
    if (!lastLog || !lastLog.details) return null;
    
    try {
      const details = JSON.parse(lastLog.details);
      return details;
    } catch {
      return null;
    }
  };

  const generateMissingCardHolders = async () => {
    const missingInfo = getMissingCardHoldersInfo();
    if (!missingInfo || !confirm(`Generate ${missingInfo.totalMissing} missing CardHolders for this job?`)) return;

    setCreatingCards(true);
    try {
      const response = await fetch(`/api/jobs/${selectedJob.id}/generate-missing-cardholders`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error);
      }

      alert(`CardHolder generation started! ${missingInfo.totalMissing} cards being created.`);
      fetchJobDetails(selectedJob.id);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setCreatingCards(false);
    }
  };

  const fetchCardAvailability = async (jobId) => {
    try {
      const response = await fetch(`/api/jobs/${jobId}/card-availability`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCardAvailability(data);
      }
    } catch (err) {
      console.error('Error fetching card availability:', err);
    }
  };

  const createMissingCardHolders = async (jobId) => {
    if (!confirm('Create all missing cardholders for this job? This will start a background job using Bluebanc API.')) return;

    setCreatingCards(true);
    try {
      const response = await fetch('/api/cardholder-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ jobId })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error);
      }

      alert('CardHolder creation job started! Check CardHolders menu for real-time progress.');
      fetchJobDetails(jobId);
      fetchCardAvailability(jobId);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setCreatingCards(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      running: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      paused: 'bg-gray-100 text-gray-800'
    };
    return badges[status] || badges.pending;
  };

  const getLogTypeColor = (type) => {
    const colors = {
      info: 'text-blue-600',
      success: 'text-green-600',
      error: 'text-red-600',
      warning: 'text-orange-600'
    };
    return colors[type] || colors.info;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Jobs</h2>
        <p className="text-gray-600 mt-2">Monitor traffic distribution jobs</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading jobs...</p>
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No jobs created yet</p>
          <p className="text-sm text-gray-400 mt-2">Create a job from Friendlies US menu</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Jobs List */}
          <div className="space-y-4">
            {jobs.map((job) => {
              const isExpanded = expandedJobs.has(job.id);
              const selectedGws = job.selectedGateways ? JSON.parse(job.selectedGateways) : [];
              const cardsPerGw = job.cardsPerGateway ? JSON.parse(job.cardsPerGateway) : {};
              const gwRates = job.gatewayRates ? JSON.parse(job.gatewayRates) : {};
              const gwQuotas = job.gatewayQuotas ? JSON.parse(job.gatewayQuotas) : {};
              const jobPricePoints = job.pricePoints ? JSON.parse(job.pricePoints) : [];
              
              return (
              <div
                key={job.id}
                className={`bg-white rounded-lg shadow-sm border p-6 transition-smooth ${
                  selectedJob?.id === job.id ? 'border-primary-500 ring-2 ring-primary-100' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-gray-900 cursor-pointer" onClick={() => setSelectedJob(job)}>{job.name}</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newExpanded = new Set(expandedJobs);
                        if (isExpanded) {
                          newExpanded.delete(job.id);
                        } else {
                          newExpanded.add(job.id);
                        }
                        setExpandedJobs(newExpanded);
                      }}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      {isExpanded ? '▼' : '▶'} Expand
                    </button>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(job.status)}`}>
                      {job.status}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Progress:</span>
                    <span className="font-medium">{job.completedTransactions}/{job.totalTransactions} ({job.progress.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{ width: `${job.progress}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="text-gray-900">{new Date(job.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Logs:</span>
                    <span className="text-gray-900">{job._count?.logs || 0}</span>
                  </div>
                  
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div className="text-xs">
                        <div className="font-medium text-gray-900 mb-2">⚙️ Configuration:</div>
                        <div className="space-y-1 text-gray-600">
                          <div>Duration: {job.durationDays} days</div>
                          <div>Delay: {job.secondsBetweenRequests ? `${job.secondsBetweenRequests}s (Manual)` : `Auto-calculated`}</div>
                          <div>VISA: {job.visaPercentage}% | MC: {job.mcPercentage}%</div>
                        </div>
                      </div>
                      
                      {jobPricePoints.length > 0 && (
                        <div className="text-xs">
                          <div className="font-medium text-gray-900 mb-2">💲 Price Points:</div>
                          <div className="flex flex-wrap gap-2">
                            {jobPricePoints.map((pp, idx) => (
                              <span key={idx} className="px-2 py-1 bg-green-50 border border-green-200 rounded text-green-700 font-medium">
                                ${pp.price} — {pp.percentage}%
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {(() => {
                        const remaining = Math.max(0, job.totalTransactions - job.completedTransactions);
                        if (remaining === 0) return null;
                        let fundingNeeded = 0;
                        let feeNeeded = 0;
                        jobPricePoints.forEach(pp => {
                          const cards = Math.round(remaining * (pp.percentage / 100));
                          fundingNeeded += cards * pp.price;
                          if (pp.price < 5) feeNeeded += cards * 0.10;
                        });
                        const activationNeeded = remaining * 0.30;
                        const totalNeeded = fundingNeeded + feeNeeded + activationNeeded;
                        return (
                          <div className="text-xs bg-indigo-50 border border-indigo-200 rounded p-3">
                            <div className="font-medium text-indigo-900 mb-1">💰 Remaining Balance Needed</div>
                            <div className="text-2xl font-bold text-indigo-600">${totalNeeded.toFixed(2)}</div>
                            <div className="text-indigo-500 mt-1 space-y-0.5">
                              <div>{remaining} txns remaining — Funding: ${fundingNeeded.toFixed(2)} + Activation: ${activationNeeded.toFixed(2)}{feeNeeded > 0 ? ` + Fee: $${feeNeeded.toFixed(2)}` : ''}</div>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="text-xs">
                        <div className="font-medium text-gray-900 mb-2">🎯 Gateway Distribution:</div>
                        <div className="space-y-1">
                          {selectedGws.map(gwId => {
                            const gw = gateways.find(g => g.id === gwId);
                            // Handle both nested format {"visa_3.99": 8} and legacy flat format (number)
                            const sentData = cardsPerGw[gwId];
                            const sent = typeof sentData === 'object' && sentData !== null
                              ? Object.values(sentData).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0)
                              : (sentData || 0);
                            const hasQuotas = gwQuotas && Object.keys(gwQuotas).length > 0;
                            const target = hasQuotas ? (gwQuotas[gwId] || 0) : null;
                            const remaining = target !== null ? Math.max(0, target - sent) : '?';
                            
                            return (
                              <div key={gwId} className="flex justify-between items-center">
                                <span className="text-gray-700 truncate max-w-[200px]" title={gw?.name}>{gw?.name || gwId}</span>
                                <span className="font-mono text-xs">
                                  {target !== null ? (
                                    <>
                                      <span className={sent >= target ? 'text-green-600' : 'text-orange-600'}>
                                        {sent}/{target}
                                      </span>
                                      <span className="text-gray-500 ml-1">({remaining} remaining)</span>
                                    </>
                                  ) : (
                                    <span className="text-gray-600">{sent} sent</span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>

          {/* Job Details & Logs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {selectedJob ? (
              <>
                <h3 className="font-bold text-lg mb-4">{selectedJob.name}</h3>
                <div className="mb-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(selectedJob.status)}`}>
                      {selectedJob.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Progress:</span>
                    <span className="font-medium">{selectedJob.completedTransactions}/{selectedJob.totalTransactions}</span>
                  </div>
                </div>

                {(selectedJob.status === 'draft' || selectedJob.status === 'pending') && cardAvailability.length > 0 && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Card Availability</h4>
                    <div className="space-y-2 text-sm">
                      {cardAvailability.map((ca, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-gray-700">{ca.cardType.toUpperCase()}</span>
                          <div>
                            <span className={`font-medium ${ca.missing > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {ca.required}/{ca.available} available
                            </span>
                            {ca.missing > 0 && <span className="ml-2 text-xs text-red-600">({ca.missing} missing)</span>}
                            {ca.missing === 0 && ca.available > ca.required && <span className="ml-2 text-xs text-green-600">(+{ca.available - ca.required} extra)</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-2">
                      <button
                        disabled={cardAvailability.every(ca => ca.missing === 0) || creatingCards}
                        onClick={() => createMissingCardHolders(selectedJob.id)}
                        className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {creatingCards ? '⏳ Creating...' : `🔧 Create Missing CardHolders (${cardAvailability.reduce((sum, ca) => sum + ca.missing, 0)})`}
                      </button>
                      <button
                        onClick={async () => {
                          if (!confirm('Start job execution? This will begin processing NMI transactions.')) return;
                          try {
                            const response = await fetch(`/api/jobs/${selectedJob.id}/start`, {
                              method: 'POST',
                              credentials: 'include'
                            });
                            if (response.ok) {
                              alert('Job started! Transactions will be processed in background.');
                              fetchJobDetails(selectedJob.id);
                            }
                          } catch (err) {
                            alert(`Error: ${err.message}`);
                          }
                        }}
                        disabled={cardAvailability.some(ca => ca.missing > 0)}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        title={cardAvailability.some(ca => ca.missing > 0) ? 'Please create missing CardHolders first' : 'Click to start job execution'}
                      >
                        {cardAvailability.some(ca => ca.missing > 0) 
                          ? `⏸ Start Job (${cardAvailability.reduce((sum, ca) => sum + ca.missing, 0)} cards missing)` 
                          : '▶ Start Job'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-4 space-y-2">
                  {selectedJob.status === 'running' && (
                    <button
                      onClick={async () => {
                        if (!confirm('Pause this job? You can resume it later.')) return;
                        try {
                          const response = await fetch(`/api/jobs/${selectedJob.id}/pause`, {
                            method: 'POST',
                            credentials: 'include'
                          });
                          if (response.ok) {
                            alert('Job paused successfully');
                            fetchJobDetails(selectedJob.id);
                          }
                        } catch (err) {
                          alert(`Error: ${err.message}`);
                        }
                      }}
                      className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                    >
                      ⏸ Pause Job
                    </button>
                  )}
                  
                  {selectedJob.status === 'paused' && getMissingCardHoldersInfo() && (
                    <>
                      {activeCreationJobs.some(j => j.name.includes(selectedJob.name)) ? (
                        <div className="w-full px-4 py-2 bg-blue-50 text-blue-800 rounded-lg border border-blue-200 text-center text-sm">
                          ⏳ CardHolder generation in progress...
                        </div>
                      ) : (
                        <button
                          onClick={generateMissingCardHolders}
                          disabled={creatingCards}
                          className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                        >
                          {creatingCards ? '⏳ Creating...' : `🔧 Generate ${getMissingCardHoldersInfo().totalMissing} Missing CardHolders`}
                        </button>
                      )}
                    </>
                  )}
                  
                  {selectedJob.status === 'paused' && (
                    <button
                      onClick={async () => {
                        if (!confirm('Resume this job? Transactions will continue from where they were paused.')) return;
                        try {
                          const response = await fetch(`/api/jobs/${selectedJob.id}/start`, {
                            method: 'POST',
                            credentials: 'include'
                          });
                          if (response.ok) {
                            alert('Job resumed successfully');
                            fetchJobDetails(selectedJob.id);
                          }
                        } catch (err) {
                          alert(`Error: ${err.message}`);
                        }
                      }}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      ▶ Resume Job
                    </button>
                  )}
                  
                  {(selectedJob.status === 'pending' || selectedJob.status === 'draft' || selectedJob.status === 'paused') && (
                    <button
                      onClick={() => deleteJob(selectedJob.id)}
                      disabled={deleting === selectedJob.id}
                      className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
                    >
                      {deleting === selectedJob.id ? '⏳ Deleting...' : '🗑️ Delete Job'}
                    </button>
                  )}
                </div>

                {/* Scheduled Transactions Button + Table */}
                {!selectedJob.secondsBetweenRequests && (
                  <div className="mb-4">
                    <button
                      onClick={async () => {
                        if (scheduleData?.jobId === selectedJob.id) {
                          setScheduleData(null);
                          return;
                        }
                        setLoadingSchedule(true);
                        try {
                          const res = await fetch(`/api/jobs/${selectedJob.id}/schedule`, { credentials: 'include' });
                          if (res.ok) {
                            const data = await res.json();
                            setScheduleData({ ...data, jobId: selectedJob.id });
                          }
                        } catch (err) { console.error(err); }
                        finally { setLoadingSchedule(false); }
                      }}
                      disabled={loadingSchedule}
                      className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
                    >
                      {loadingSchedule ? '⏳ Loading...' : scheduleData?.jobId === selectedJob.id ? '📅 Hide Schedule' : '📅 Scheduled Transactions (PT)'}
                    </button>

                    {scheduleData?.jobId === selectedJob.id && scheduleData.breakdown && (
                      <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded p-3 max-h-[300px] overflow-y-auto">
                        <div className="text-xs font-medium text-indigo-900 mb-2">
                          📅 PT Schedule — {scheduleData.durationDays} days, {scheduleData.totalTransactions} txns
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-indigo-700 border-b border-indigo-200">
                              <th className="text-left py-1 px-2">Date</th>
                              <th className="text-left py-1 px-2">Hour (PT)</th>
                              <th className="text-right py-1 px-2">Planned</th>
                              <th className="text-right py-1 px-2">Sent</th>
                              <th className="text-right py-1 px-2">Remaining</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scheduleData.breakdown.map((row, i) => {
                              const remaining = row.planned - row.sent;
                              const isPast = row.sent >= row.planned;
                              return (
                                <tr key={i} className={`border-b border-indigo-100 ${isPast ? 'text-gray-400' : remaining === 0 ? 'text-green-600' : ''}`}>
                                  <td className="py-1 px-2">{row.date}</td>
                                  <td className="py-1 px-2">{row.hour}:00</td>
                                  <td className="py-1 px-2 text-right font-mono">{row.planned}</td>
                                  <td className="py-1 px-2 text-right font-mono font-bold">{row.sent}</td>
                                  <td className="py-1 px-2 text-right font-mono">{remaining > 0 ? remaining : '✅'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-indigo-300 font-bold text-indigo-800">
                              <td colSpan={2} className="py-1 px-2">Total</td>
                              <td className="py-1 px-2 text-right">{scheduleData.breakdown.reduce((s, r) => s + r.planned, 0)}</td>
                              <td className="py-1 px-2 text-right">{scheduleData.breakdown.reduce((s, r) => s + r.sent, 0)}</td>
                              <td className="py-1 px-2 text-right">{scheduleData.breakdown.reduce((s, r) => s + (r.planned - r.sent), 0)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                <h4 className="font-medium text-gray-900 mb-2">Logs</h4>
                <div className="bg-gray-50 rounded border border-gray-200 p-4 max-h-[500px] overflow-y-auto">
                  {logs.length === 0 ? (
                    <p className="text-sm text-gray-500">No logs yet</p>
                  ) : (
                    <div className="space-y-2">
                      {logs.map((log) => (
                        <div key={log.id} className="text-sm">
                          <span className="text-gray-500">{new Date(log.createdAt).toLocaleTimeString()}</span>
                          <span className={`ml-2 font-medium ${getLogTypeColor(log.type)}`}>[{log.type.toUpperCase()}]</span>
                          <span className="ml-2 text-gray-900">{log.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">Select a job to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
